import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wallet, CheckCircle2, Clock, ArrowUpRight } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const MONTH_NAMES = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic',
];

function formatEUR(n) {
  return `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * CollectionsDashboardCard — Riepilogo incassi mese corrente + 2 mesi precedenti.
 * Visibile solo per admin/amministrativo. Link rapido a /app/incassi.
 */
export default function CollectionsDashboardCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/collections/dashboard');
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="p-4 border-border" data-testid="collections-dashboard-card-loading">
        <div className="text-xs text-muted-foreground">{t('collections.loading', 'Caricamento incassi…')}</div>
      </Card>
    );
  }
  if (!data) return null;

  const months = data.months || [];
  const current = months[0] || {};

  return (
    <Card className="p-4 border-border" data-testid="collections-dashboard-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[hsl(var(--primary))]" />
          <div className="font-medium text-sm">{t('collections.monthTitle', 'Incassi del mese')}</div>
        </div>
        <Link
          to="/app/incassi"
          data-testid="collections-dashboard-link"
          className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1"
        >
          {t('collections.open', 'Apri Incassi')} <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Current month main tile */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded bg-[var(--surface-2)] p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {MONTH_NAMES[(current.month || 1) - 1]} {current.year || ''}
          </div>
          <div className="text-xl font-semibold tabular-nums mt-0.5" data-testid="collections-current-total">
            {formatEUR(current.total_amount)}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {current.installments_count || 0} rate
          </div>
        </div>
        <div className="rounded bg-emerald-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700">Incassato</div>
          <div className="text-xl font-semibold tabular-nums text-emerald-800 mt-0.5">
            {formatEUR(current.paid_amount)}
          </div>
        </div>
        <div className="rounded bg-[var(--surface-2)] p-3 flex flex-col justify-center">
          {current.status === 'approved' ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 w-fit">
              <CheckCircle2 className="w-3 h-3 mr-1" /> {t('collections.approved', 'Approvato')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-700 border-amber-300 w-fit">
              <Clock className="w-3 h-3 mr-1" /> {t('commissions.status.pending', 'In attesa')}
            </Badge>
          )}
          {current.approved_by_name && (
            <div className="text-[10px] text-muted-foreground mt-1 truncate">
              {t('collections.by', 'da')} {current.approved_by_name}
            </div>
          )}
        </div>
      </div>

      {/* Previous 2 months mini-row */}
      {months.length > 1 && (
        <div className="grid grid-cols-2 gap-2 pt-2 border-t">
          {months.slice(1).map((m) => (
            <div key={`${m.year}-${m.month}`} className="flex items-center justify-between text-xs px-2 py-1">
              <span className="text-muted-foreground">
                {MONTH_NAMES[(m.month || 1) - 1]} {m.year}
              </span>
              <span className="tabular-nums">
                {formatEUR(m.total_amount)}
                <span className="ml-2 text-[10px] text-muted-foreground">{m.installments_count || 0} rate</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
